/// <amd-module name="Test/PassRateChartView/Services/PassRateSourceService" />
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { IAnalyticsExtensionStateService } from "Analytics/Shared/ExtensionService/AnalyticsExtensionService";
import { axServiceInstanceId, emsServiceInstanceId } from "Test/Analytics/Common/Constants";
import { WorkflowConfigurationProps } from "Test/Analytics/Common/Definitions";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { getTestAnalyticsPageUrl } from "Test/Analytics/Common/Utils/TestAnalyticsURLUtils";
import { TestReportDataParser } from "Test/common-util/Common.Utils";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";
import * as Resources from "Test/PassRateChartView/Resources";
import { PassRateChartSource } from "Test/PassRateChartView/Sources/PassRateChartSource";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext, Services, TeamFoundationHostType, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLocationService } from "VSS/Platform/Location";

export class PassRateSourceService extends VssService implements CommonTypes.IPassRateChartSource {
    private _passRateChartSource: PassRateChartSource;
    private _pageContext: IVssPageContext;

    public initialize(command: string, pageContext: IVssPageContext) {
        this._pageContext = pageContext;
        if (isFeatureFlagEnabled(pageContext, PipelineEntities.RenameBuildEntitiesFeatureFlag)) {
            PipelineEntities.switchEntityAndAttributeNames();
        }

        this._passRateChartSource = new PassRateChartSource(pageContext);
    }

    public async getTestAnalyticsLinkData(buildDefinitionId: number): Promise<CommonTypes.ITestAnalyticsLinkData> {
        const testAnalyticsLinkData = {} as CommonTypes.ITestAnalyticsLinkData;

        const vssLocationService: IVssLocationService = this._pageContext.getService<IVssLocationService>("IVssLocationService");

        const emsServicePromise: Promise<string> = vssLocationService.getServiceLocationAsync(
            emsServiceInstanceId,
            TeamFoundationHostType.ProjectCollection
        );
        const axServicePromise: Promise<string> = vssLocationService.getServiceLocationAsync(
            axServiceInstanceId,
            TeamFoundationHostType.ProjectCollection
        );

        return Promise.all([emsServicePromise, axServicePromise]).then(async result => {
            try {
                const contributionService: IVssContributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
                await contributionService.getContributionAsync("ms.vss-analyticsshared-web.analytics-extension-service");

                const analyticsExtensionStateService = this._pageContext.getService<IAnalyticsExtensionStateService>(
                    "IAnalyticsExtensionStateService"
                );
                const isAnalyticsEnabled = await analyticsExtensionStateService.isAnalyticsEnabled();
                if (isAnalyticsEnabled) {
                    const testReportContext: CommonTypes.ITestReportContext = {
                        contextType: TCMContracts.TestResultsContextType.Build,
                        definitionId: buildDefinitionId
                    };
                    const confValues: CommonTypes.IReportConfiguration = {
                        groupBy: CommonTypes.GroupBy.None,
                        trendBy: CommonTypes.TrendBy.Days,
                        period: CommonTypes.Period.Days_14,
                        outcomes: [],
                        configuredFilters: {
                            [CommonTypes.Filter.Workflow]: {
                                values: [
                                    new FilterValueItem(
                                        CommonTypes.Workflow.Build.toString(),
                                        new WorkflowConfigurationProps().options[CommonTypes.Workflow.Build]
                                    )
                                ]
                            }
                        }
                    };
                    const passRateChartData = await this.queryTestOutcomeCountMetricData(testReportContext, confValues);
                    testAnalyticsLinkData.testAnalyticsLinkMessage = Utils_String.format(
                        Resources.TestAnalyticsLinkMessage,
                        TestReportDataParser.getPercentageInDisplayFormat(Number(passRateChartData.passPercentage), 2)
                    );
                }
            } catch (error) {
                if (error.message) {
                    console.error(JSON.stringify(error.message));
                } else {
                    console.error(JSON.stringify(error));
                }
            }

            testAnalyticsLinkData.testAnalyticsLink = getTestAnalyticsPageUrl(this._pageContext, {
                contextType: TCMContracts.TestResultsContextType.Build,
                definitionId: buildDefinitionId
            });

            return testAnalyticsLinkData;
        });
    }

    private queryTestOutcomeCountMetricData(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.ICardMetricsData> {
        return this._passRateChartSource.queryTestOutcomeCountMetricData(testReportContext, confValues);
    }
}

Services.add("PassRateSourceService", { serviceFactory: PassRateSourceService });
